import { Toaster } from '@w/fe-ui/components/sonner'
import { initI18n, useTranslation, useTranslatedText } from '@w/fe-inv-i18n'
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense } from 'react'
import { createPortal } from 'react-dom'
import { ThemeProvider } from '@w/fe-ui/components/theme-provider'
import { ErrorBoundaryConfig } from '@w/fe-ui/system/error-boundary'
import { publicUrl } from '../lib/utils'
import { Button } from '@w/fe-ui/components/button'
import { useMfRootElement } from '@w/fe-ui/lib/utils'
import { Link } from '@tanstack/react-router'
import { getQueryClient } from '../api/query-client'
import { getRouteTel } from '@w/fe-utils'
import { UserAnalyticsProvider } from '../user-analytics/use-user-analytics'
import { configurePlatformI18n } from '@w/fe-i18n-platform'
import { AuthSessionBoundary } from '../auth/auth-session-notice'

initI18n()

configurePlatformI18n(useTranslation, useTranslatedText)

ErrorBoundaryConfig.ErrorFallbackIllustration = () => (
  <img src={publicUrl('/satellite-dish-down.svg')} alt="Error illustration" className="w-[140px]" />
)
ErrorBoundaryConfig.RetriableErrorFallbackIllustration = () => (
  <img src={publicUrl('/satellite-dish-down.svg')} alt="Error illustration" className="w-[140px]" />
)
function ErrorFallbackAction() {
  const { t } = useTranslation('platform') // extracted as a function to satisfy eslint
  return <Button asChild>{<Link to="..">{t('backToOverview')}</Link>}</Button>
}
ErrorBoundaryConfig.ErrorFallbackAction = ErrorFallbackAction
// ErrorBoundaryConfig.ErrorBoundary = Sentry.ErrorBoundary
ErrorBoundaryConfig.onError = (error, info) => getRouteTel().onCatch(error, info)

/**
 * Toasts are portalled to the app's root for the same reason as the line-item selection bar: the
 * app's scroll container (app.tsx) carries a transform, which would make it the containing block
 * of the toaster's `fixed` position (toasts scroll away) and trap its z-index below anything
 * portalled out of it.
 */
function AppToaster() {
  return createPortal(<Toaster />, useMfRootElement(document.body))
}

interface ShellProps {
  children: React.ReactNode
  reversedTheme?: boolean // for storybook
  queryClient?: QueryClient
}

export function BaseShell({ children, reversedTheme, ...props }: ShellProps) {
  return (
    <QueryClientProvider client={getQueryClient(props)}>
      <UserAnalyticsProvider>
        <ThemeProvider storageKey="vite-ui-theme" reverse={reversedTheme}>
          <AuthSessionBoundary>
            <Suspense fallback={<div>loading app...</div>}>
              {children}
              <AppToaster />
            </Suspense>
          </AuthSessionBoundary>
        </ThemeProvider>
      </UserAnalyticsProvider>
    </QueryClientProvider>
  )
}
